import React, { useState } from 'react';
import { FileText, Loader } from 'lucide-react';
import { aiAPI } from '../services/api';
import toast from 'react-hot-toast';
import MarkdownRenderer from './MarkdownRenderer';
import StatsAndTabs from './StatsAndTabs';

const DocumentAnalysis = ({ onSuccess, stats, loading: statsLoading }) => {
  const [formData, setFormData] = useState({
    documentName: '',
    documentContent: ''
  });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState('');

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.documentName.trim() || !formData.documentContent.trim()) {
      toast.error('Please fill in all fields');
      return;
    }

    setLoading(true);
    setResult('');

    try {
      const response = await aiAPI.analyzeDocument({
        documentName: formData.documentName,
        documentContent: formData.documentContent
      });

      setResult(response.result);
      toast.success('Document analysis completed!');
      
      // Call onSuccess to refresh dashboard stats
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      console.error('Document analysis failed:', error);
      toast.error('Failed to analyze document');
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setFormData({
      documentName: '',
      documentContent: ''
    });
    setResult('');
  };

  return (
    <div>
      <div className="content-header">
        <div className="welcome-section">
          <h2>Document Analysis</h2>
          <p>Analyze legal documents with AI-powered insights</p>
        </div>
      </div>

      <StatsAndTabs stats={stats} loading={statsLoading} />

      <div className="tool-content">
        <div className="tool-header">
          <FileText size={24} />
          <h3>Document Analysis</h3>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">Document Name</label>
            <input
              type="text"
              name="documentName"
              className="form-input"
              placeholder="Enter document name..."
              value={formData.documentName}
              onChange={handleChange}
            />
          </div>

          <div className="form-group">
            <label className="form-label">Document Content</label>
            <textarea
              name="documentContent"
              className="form-textarea"
              placeholder="Paste your legal document content here..."
              value={formData.documentContent}
              onChange={handleChange}
              style={{ minHeight: '200px' }}
            />
          </div>

          <div style={{ display: 'flex', gap: '12px' }}>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading}
            >
              {loading ? (
                <>
                  <Loader size={16} className="animate-spin" />
                  Analyzing Document...
                </>
              ) : (
                <>
                  <FileText size={16} />
                  Analyze Document
                </>
              )}
            </button>

            <button
              type="button"
              className="btn btn-secondary"
              onClick={handleClear}
              disabled={loading}
            >
              Clear
            </button>
          </div>
        </form>
      </div>

      {result && (
        <div className="tool-content">
          <div className="tool-header">
            <FileText size={24} />
            <h3>Analysis Result</h3>
          </div>
          
          <MarkdownRenderer content={result} />

          <div style={{ marginTop: '16px' }}>
            <button
              className="btn btn-secondary"
              onClick={() => navigator.clipboard.writeText(result)}
            >
              Copy Result
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentAnalysis;
